import { Link } from '../Link/Link';
import unescapedText from 'I18n/utils/unescapedText';
import { isOptionGroup as _isOptionGroup } from './Select.types';
import styled from 'foundations-theming/utils';
import { render as customRenderer } from '../../utils/customRenderer';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { loadMoreOptionValue, stripDiacritics } from './Select.constants';
import { lazyEval } from '../../utils/lazyEval';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const LoadMoreSpan = styled.span.withConfig({
  displayName: "Selecthelpers__LoadMoreSpan",
  componentId: "sc-1x9frof-0"
})(({
  theme
}) => {
  return {
    marginInlineEnd: theme.space['100'],
    color: theme.color['text-primary-disabled']
  };
});
export const LoadMoreComponent = ({
  options,
  pagination
}) => {
  return /*#__PURE__*/_jsxs("span", {
    children: [/*#__PURE__*/_jsx(LoadMoreSpan, {
      children: pagination.length ? unescapedText('salesUI.UISelect.andMore', {
        count: pagination.length - options.length
      }) : ''
    }), /*#__PURE__*/_jsx(Link, {
      children: unescapedText('salesUI.UISelect.loadMore')
    })]
  });
};
let multiDummyOption;
export const getMultiDummyOption = () => {
  multiDummyOption = multiDummyOption || {
    disabled: true,
    dropdownText: unescapedText('salesUI.UISelect.allOptionsSelected'),
    isSpecial: true,
    title: '',
    value: '__MULTI_DUMMY_OPTION__'
  };
  return multiDummyOption;
};
export const getLoadMoreOption = ({
  input,
  loadMoreComponent,
  options,
  pagination
}) => {
  return {
    'data-load-more': true,
    dropdownText: customRenderer(loadMoreComponent, {
      options,
      pagination
    }),
    input,
    isSpecial: true,
    title: '',
    value: loadMoreOptionValue
  };
};
export const getIsLoadingOption = () => {
  return {
    disabled: true,
    loading: true,
    dropdownText: /*#__PURE__*/_jsx(LoadingSpinner, {
      paddingBlock: "100",
      layout: "centered",
      size: "sm"
    }),
    isSpecial: true,
    title: '',
    value: '__IS_LOADING_OPTION__'
  };
};
export const getInputProps = (autoComplete, disabled, id, searchable, computedAnchorType, handleAutosize, handlePaste, readOnly) => {
  if (disabled || !searchable || computedAnchorType !== 'combined' || readOnly) return {};
  return {
    autoComplete,
    id,
    onAutosize: handleAutosize,
    onPaste: handlePaste
  };
};
export const isVirtualized = menuRenderer =>
// @ts-expect-error - TODO(TS): hacky way of detecting the default virtualized menu renderer
menuRenderer && menuRenderer.displayName === 'virtualizedMenuRenderer';

/**
 * @param option The option to determine the checkbox state for
 * @param value The `value` prop
 * @return {Object} { indeterminate, checked }
 */
export const getCheckboxState = (option, value) => {
  if (option.getCheckboxState) return option.getCheckboxState(option);
  const isSelectAll = _isOptionGroup(option) && !!option.selectAll;
  let nestedOptionCount = 0;
  let selectedOptionCount = 0;
  if (isSelectAll) {
    const tabulate = o => {
      if (_isOptionGroup(o)) {
        o.options.forEach(tabulate);
      } else if (!o.disabled) {
        nestedOptionCount += 1;
        if (Array.isArray(value) && value.includes(o.value)) selectedOptionCount += 1;
      }
    };
    tabulate(option);
  }
  const indeterminate = isSelectAll && 0 < selectedOptionCount && selectedOptionCount < nestedOptionCount;
  const checked = isSelectAll ? nestedOptionCount === selectedOptionCount : !!(Array.isArray(value) && value.includes(option.value));
  return {
    indeterminate,
    checked
  };
};
export const makeNewOptionsCache = (optionsCache, options) => {
  const newOptionsCache = Object.assign({}, optionsCache);
  const addOptionToCache = option => {
    newOptionsCache[`${option.value}`] = option;
  };
  const addOptionListToCache = optionList => {
    optionList.forEach(option => {
      // NOTE: technically UISelect enforced that `option.options` is an array,
      if (_isOptionGroup(option)) {
        addOptionListToCache(option.options);
      } else {
        addOptionToCache(option);
      }
    });
  };
  // Recursively add all options, including those within groups, to the cache.
  addOptionListToCache(options);
  return newOptionsCache;
};
export function promptTextCreator(label) {
  return unescapedText('salesUI.UISelect.createOption', {
    label
  });
}

/**
 * "Cleans" an input value. Matches the filter react-select-plus applies to the input passed to `loadOptionsProxy`. (#5289)
 */
export function filterInputValue(inputValue, {
  ignoreAccents = true,
  ignoreCase = true
}) {
  // https://github.com/HubSpot/react-select-plus/blob/8ae7465fc9b6b9600a01e4e23de97f2d403fd139/src/Async.js#L155-L163
  let transformedInputValue = inputValue;
  if (ignoreAccents) {
    transformedInputValue = stripDiacritics(transformedInputValue);
  }
  if (ignoreCase) {
    transformedInputValue = transformedInputValue.toLowerCase();
  }
  return transformedInputValue;
}
export const getOptionsCache = ({
  options
}, {
  optionsCache
}) => {
  // Cache options provided via props (required for certain async scenarios, and for paste handling)
  if (options) {
    const newOptionsCache = makeNewOptionsCache(optionsCache, options);
    return {
      optionsCache: newOptionsCache
    };
  }
  return null;
};
export const getPaginationCache = ({
  cache
}) => {
  // If async caching is disabled, paginationCache should not persist across searches
  if (cache === null || cache === false) {
    return {
      paginationCache: {}
    };
  }
  return null;
};
export const getPlaceholder = ({
  isDisplayOptimized,
  placeholder
}) => {
  if (placeholder !== undefined) {
    return placeholder;
  }
  if (isDisplayOptimized) {
    return '--';
  }
  return unescapedText('salesUI.UISelect.placeholder');
};
export const getResetOption = ({
  isDisplayOptimized,
  placeholder,
  resetValue
}, {
  resetOption
}) => {
  if (isDisplayOptimized) {
    return null;
  }
  const placeholderText = getPlaceholder({
    isDisplayOptimized,
    placeholder
  });
  const newResetOption = {
    buttonText: lazyEval(placeholderText),
    dropdownText: lazyEval(placeholderText),
    value: resetValue
  };
  if (!(resetOption && resetOption.buttonText === newResetOption.buttonText && resetOption.value === newResetOption.value)) {
    return {
      resetOption: newResetOption
    };
  }
  return null;
};
export const computeControlWidth = (controlEl, open) => {
  if (!(controlEl instanceof Element) || !open) {
    return 0;
  }
  const exactWidth = controlEl.getBoundingClientRect().width;
  const roundedWidth = window.chrome ? Math.floor(exactWidth) : exactWidth;
  return roundedWidth - 2;
};